import * as LabelPrimitive from '@radix-ui/react-label';
import { forwardRef, useId } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export const Label = forwardRef<
  HTMLLabelElement,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> & { required?: boolean }
>(function Label({ className, required, children, ...props }, ref) {
  return (
    <LabelPrimitive.Root
      ref={ref}
      className={cn('text-[13px] leading-none font-medium text-ink-2 select-none', className)}
      {...props}
    >
      {children}
      {required ? (
        <span className="ml-0.5 text-danger" aria-hidden>
          *
        </span>
      ) : null}
    </LabelPrimitive.Root>
  );
});

// Mobil 44px hoch und 16px Schrift, ab `sm` 38px/14px – dieselbe Stufe wie
// `md` beim Knopf (siehe die Tabelle in `button.tsx`). Steht ein Feld neben
// einem Knopf, sitzen beide damit auf einer Linie.
//
// Die 16px auf Mobil sind Pflicht, nicht Geschmack: iOS Safari zoomt sonst
// beim Fokus in die Seite hinein (siehe index.css). `text-base` steht hier
// trotzdem, damit Höhe und Schrift zusammenpassen, statt sich auf die globale
// Regel zu verlassen.
const CONTROL_SIZE = 'h-11 text-base sm:h-9.5 sm:text-sm';

// `rounded-control` (14px) statt Vollrund: bei linksbündigem Text müsste der
// Innenabstand mit der Rundung mitwachsen, und in dichten Formularen kostet
// das Platz, das keiner hat. Vollrund tragen Knöpfe, Chips und die Suche.
const CONTROL_BASE = cn(
  'w-full rounded-control border border-line bg-surface text-ink shadow-inset',
  'placeholder:text-ink-3',
  'transition-[border-color,box-shadow] duration-150',
  'hover:border-line-strong',
  'focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25',
  'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-3 disabled:shadow-none',
  'aria-invalid:border-danger aria-invalid:focus:ring-danger/25',
);

export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={cn(CONTROL_BASE, CONTROL_SIZE, 'px-3.5', className)} {...props} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, rows = 4, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        CONTROL_BASE,
        'resize-y px-3.5 py-2.5 text-base leading-relaxed sm:py-2 sm:text-sm',
        className,
      )}
      {...props}
    />
  );
});

/** Schlichtes Select auf Basis des nativen Elements – auf Mobil das beste. */
export const NativeSelect = forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement> & {
    /**
     * Zeichen vor der Beschriftung – etwa ein Sortierpfeil.
     *
     * Als Eigenschaft und nicht daneben gelegt: ein absolut gesetztes Symbol
     * *vor* dem Select steht im Baum früher und wird deshalb von dessen
     * Fläche überdeckt. Hier liegt es danach, im selben Kasten, und sitzt
     * damit sichtbar darüber.
     */
    icon?: React.ReactNode;
  }
>(function NativeSelect({ className, children, icon, ...props }, ref) {
  return (
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          CONTROL_BASE,
          CONTROL_SIZE,
          'appearance-none pr-9',
          icon ? 'pl-9' : 'pl-3.5',
          '[&>option]:bg-surface [&>option]:text-ink',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {icon ? (
        <span className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-ink-3 [&>svg]:size-4">
          {icon}
        </span>
      ) : null}
      <svg
        className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-ink-3"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden
      >
        <path d="m4 6 4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
});

export interface FieldProps {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: string | undefined;
  required?: boolean;
  className?: string;
  /** Bekommt id und aria-Attribute übergeben. */
  children: (props: {
    id: string;
    'aria-invalid': boolean | undefined;
    'aria-describedby': string | undefined;
  }) => React.ReactNode;
}

/** Label + Steuerelement + Hinweis/Fehler, korrekt verdrahtet. */
export function Field({ label, hint, error, required, className, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label ? (
        <Label htmlFor={id} required={required}>
          {label}
        </Label>
      ) : null}
      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
      })}
      {error ? (
        <p id={errorId} className="flex items-start gap-1 text-[12.5px] text-danger">
          <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden />
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-[12.5px] text-ink-3">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
